import React, { Suspense, useEffect, useState } from 'react';

import { Auth, Hub } from 'aws-amplify';
import { useLocation, useNavigate } from 'react-router-dom';

import Loader from 'components/shared/Loader';
import { AUTH_CHANNEL, AUTH_EVENT } from 'constants/amplify';
import { NON_AUTHENTICATED_PATHS, PATHS } from 'constants/pathsB2c';
import { lazyWithRetry } from 'services/helpers/lazyWithRetry';
import { useB2cAuthEventListener } from 'services/hooks/useB2cAuthEventListener';
import { HubCallback } from 'shared-types/amplify';

import { AuthState } from './withAuthentication.const';

const SignIn = lazyWithRetry(() => import('./components/SignIn'));
const SignUp = lazyWithRetry(() => import('./components/SignUp'));
const MicrophoneSignUp = lazyWithRetry(() => import('./components/MicrophoneSignUp'));

const withAuthentication = <T extends object>(Page: React.FunctionComponent<T>) => {
  const AuthenticatedPage: React.FunctionComponent<T> = (props: T) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [authState, setAuthState] = useState<AuthState>(AuthState.SIGN_IN);

    const { handleSignIn, handleSignOut } = useB2cAuthEventListener();

    const AUTH_STATE_TO_COMPONENT_MAPPING: Record<AuthState, React.ReactElement> = {
      [AuthState.SIGN_IN]: <SignIn />,
      [AuthState.SIGN_UP]: (
        <SignUp titleTranslationId='sign-up.title' subtitleTranslationIds={['sign-up.subtitle']} />
      ),
      [AuthState.FREE_SIGN_UP]: (
        <SignUp
          titleTranslationId='free-sign-up.title'
          subtitleTranslationIds={['free-sign-up.subtitle1', 'free-sign-up.subtitle2']}
        />
      ),
      [AuthState.MICROPHONE_SIGN_UP]: <MicrophoneSignUp />,
      [AuthState.SIGNED_IN]: <Page {...props} />,
    };

    useEffect(() => {
      const authListener: HubCallback = async ({ payload }) => {
        switch (payload.event) {
          case AUTH_EVENT.SIGN_IN: {
            const userId = payload?.data?.attributes.sub;
            if (location.state?.authState === AuthState.MICROPHONE_SIGN_UP) {
              /*
                We want to redirect to stripe checkout after microphone sign in, so we block classic redirect here.
                It avoids seeing offers page before stripe redirect
              */
              return;
            }
            handleSignIn(userId);
            setAuthState(AuthState.SIGNED_IN);
            break;
          }
          case AUTH_EVENT.SIGN_OUT:
            setAuthState(AuthState.SIGN_IN);
            handleSignOut();
            navigate(PATHS.HOME);
            break;
          default:
            break;
        }
      };
      Hub.listen(AUTH_CHANNEL, authListener);
      return () => Hub.remove(AUTH_CHANNEL, authListener);
    }, [handleSignIn, handleSignOut, location, navigate]);

    useEffect(() => {
      const getCurrentUser = async () => {
        try {
          await Auth.currentAuthenticatedUser();
          setAuthState(AuthState.SIGNED_IN);
        } catch {
          const requiredState = location.state?.authState ?? AuthState.SIGN_IN;
          setAuthState(requiredState);
        }
      };
      getCurrentUser();
    }, [location, setAuthState]);

    if (NON_AUTHENTICATED_PATHS.includes(location.pathname)) {
      return <Page {...props} />;
    }

    return <Suspense fallback={<Loader />}>{AUTH_STATE_TO_COMPONENT_MAPPING[authState]}</Suspense>;
  };

  return AuthenticatedPage;
};

export default withAuthentication;
